import { useContext, useEffect, useRef, useState } from "preact/hooks";
import type { LiveBreakdown } from "../../types/generated/live";
import { Box } from "../ui/box";
import { Icon, type IconName, isIconName } from "../ui/icon";
import { NumberField } from "../ui/number_field";
import { RollChip, type RollSpec } from "../ui/roll_chip";
import { StatusBand } from "../ui/status_band";
import { Term } from "../ui/term";
import { initiativeStat, partLabel, sumLine } from "./breakdown";
import { ACTION_ROW, Button, TextButton } from "./controls";
import { EditContext, type Edits } from "./edits";
import { signed } from "./format";
import { attributeTerm, modifierTerm, type TermInfo, termInfo } from "./glossary";
import { interpolate, type Labels } from "./labels";
import { Bar, Pop } from "./pop";
import { useOpenPopup } from "./popups";
import { attributeRoll, baseAttackRolls, testRoll } from "./rolls";
import type { SheetView } from "./sheet_view";

export function Attributes({ view, labels }: { view: SheetView; labels: Labels }) {
	return (
		<>
			{view.sheet.attributes.map((attribute) => {
				const name = attributeTerm(attribute, labels);
				const modifier = modifierTerm(attribute, labels);
				return (
					<Box
						key={attribute.key}
						icon={isIconName(attribute.key) ? attribute.key : "dice20"}
						title={<Term label={labels.attribute_names[attribute.key]} title={name.title} text={name.text} lines={name.lines} />}
						titleClass="text-[15px]"
						class="cs-attr"
					>
						<div class="flex h-full flex-col items-center justify-center gap-1.5">
							<RollChip size="xl" spec={attributeRoll(attribute, labels)}>
								<Pop value={attribute.score ?? "-"} class="cs-num" />
							</RollChip>
							<Term
								class="text-[13px]"
								label={interpolate(labels.boxes.modifier, { value: signed(attribute.modifier) })}
								title={modifier.title}
								lines={modifier.lines}
							/>
						</div>
					</Box>
				);
			})}
		</>
	);
}

export function HealthPoints({ view, labels }: { view: SheetView; labels: Labels }) {
	const { sheet, derived } = view;
	const edits = useContext(EditContext);
	const max = derived.max_hp.total;
	return (
		<>
			<Box icon="heart" title={labels.terms.hit_points.full} class="cs-hp px-3 pb-3">
				<div class="mt-1 flex items-center gap-2">
					{edits ? (
						<NumberField
							value={sheet.health_points}
							min={0}
							max={max}
							disabled={sheet.dead}
							label={labels.edit.hp_current}
							steps={{ less: labels.edit.hp_less, more: labels.edit.hp_more }}
							class="cs-num h-11 w-20 rounded-lg border border-line bg-surface-raised px-2 text-center text-3xl text-ink disabled:opacity-60"
							relative
							onCommit={(next) => edits.setHealth(next, max)}
							onDelta={(delta) => edits.changeHealth(delta, max)}
						/>
					) : (
						<Pop value={sheet.health_points} class="cs-num text-4xl leading-none" />
					)}
					<span class="cs-num text-xl text-ink-muted">{interpolate(labels.boxes.of_max, { max })}</span>
					<Bar percent={max > 0 ? (Math.max(0, sheet.health_points) / max) * 100 : 0} color="bg-verde-500" label={labels.terms.hit_points.abbr} />
				</div>
				{edits && <HealthEdit view={view} max={max} edits={edits} labels={labels} />}
			</Box>
			<HealthBand view={view} labels={labels} />
		</>
	);
}

// Damage and healing by an amount, and Repouso while the character is up.
function HealthEdit({ view, max, edits, labels }: { view: SheetView; max: number; edits: Edits; labels: Labels }) {
	const { sheet } = view;
	const [amount, setAmount] = useState("");
	const open = useOpenPopup();
	const points = Number.parseInt(amount, 10);
	const apply = (sign: number) => {
		edits.setHealth(sheet.health_points + sign * points, max);
		setAmount("");
	};
	const idle = sheet.dead || !(points > 0);
	return (
		<div class={`mt-2 ${ACTION_ROW}`}>
			<input
				type="number"
				inputMode="numeric"
				min={1}
				autocomplete="off"
				aria-label={labels.edit.amount}
				placeholder="0"
				value={amount}
				disabled={sheet.dead}
				class="cs-num h-11 w-20 rounded-lg border border-line bg-surface-raised px-2 text-center text-xl text-ink"
				onInput={(event) => setAmount(event.currentTarget.value)}
			/>
			<Button tone="primary" icon="heartMinus" label={labels.edit.damage} disabled={idle} onClick={() => apply(-1)} />
			<Button tone="secondary" icon="heartPlus" label={labels.edit.heal} disabled={idle} onClick={() => apply(1)} />
			{open && !sheet.dead && sheet.health_points > 0 && (
				<Button tone="alt" icon="campfire" label={labels.rest.button} labelClass="cs-rest-label" onClick={(button) => open({ kind: "rest" }, button)} />
			)}
		</div>
	);
}

// A dying or dead character gets a band in place of a badge: its own row on a wide sheet, right after the PV box on a
// narrow one (character_sheet.css). Marking dead is confirmed here, never in a browser dialog.
function HealthBand({ view, labels }: { view: SheetView; labels: Labels }) {
	const { sheet, derived } = view;
	const edits = useContext(EditContext);
	const open = useOpenPopup();
	const [confirming, setConfirming] = useState(false);
	const trigger = useRef<HTMLButtonElement>(null);
	const confirm = useRef<HTMLButtonElement>(null);
	const unmark = useRef<HTMLButtonElement>(null);
	// Each swap of buttons would drop a focus left on one that unmounts, so the focus is handed on by hand.
	const handoff = useRef<"trigger" | "unmark" | null>(null);
	useEffect(() => {
		if (confirming) confirm.current?.focus();
		else if (handoff.current === "trigger") trigger.current?.focus();
		if (handoff.current === "trigger" && !confirming) handoff.current = null;
	}, [confirming]);
	useEffect(() => {
		if (sheet.dead && handoff.current === "unmark") {
			unmark.current?.focus();
			handoff.current = null;
		}
	}, [sheet.dead]);
	if (sheet.dead) {
		return (
			<StatusBand tone="neutral" icon="skull" title={labels.boxes.dead} text={labels.boxes.dead_hint}>
				{edits && <Button tone="alt" icon="wings" label={labels.agonize.unmark_dead} buttonRef={unmark} onClick={() => edits.setDead(false)} />}
			</StatusBand>
		);
	}
	if (derived.max_hp.total <= 0 || sheet.health_points > 0) return null;
	const markDead = () => {
		handoff.current = "unmark";
		setConfirming(false);
		edits?.setDead(true);
	};
	const cancel = () => {
		handoff.current = "trigger";
		setConfirming(false);
	};
	return (
		<StatusBand tone="bad" icon="skull" title={labels.boxes.dying} text={interpolate(labels.boxes.dying_hint, { target: derived.agonize_target.total })}>
			{edits && confirming && (
				<>
					<span role="status" class="text-sm font-bold">
						{interpolate(labels.agonize.mark_dead_confirm, { name: sheet.name })}
					</span>
					<Button tone="primary" icon="skull" label={labels.edit.mark_dead} buttonRef={confirm} onClick={markDead} />
					<TextButton label={labels.edit.cancel} onClick={cancel} />
				</>
			)}
			{edits && !confirming && (
				<>
					{open && <Button tone="primary" icon="heartInside" label={labels.agonize.button} onClick={(button) => open({ kind: "agonize" }, button)} />}
					<TextButton label={labels.edit.mark_dead} buttonRef={trigger} onClick={() => setConfirming(true)} />
				</>
			)}
		</StatusBand>
	);
}

type Tile = { label: string; narrowLabel?: string; term: TermInfo; lines: string[]; value: string | number; wideOnly?: boolean; roll?: RollSpec };
type Group = { title: string; icon: IconName; tiles: Tile[] };

const meters = (n: number) => `${n}m`;

// Each derived number, labelled by its term; the sum it is made of lives in the term's popover.
function groups(view: SheetView, labels: Labels): Group[] {
	const { derived } = view;
	const { terms, boxes } = labels;
	const sum = (breakdown: LiveBreakdown) => [sumLine(breakdown, labels)];
	const flying = derived.movement_fly;
	const flyLine = flying.total === 0 && flying.parts[0] ? partLabel(flying.parts[0], labels) : sumLine(flying, labels);
	const stat = initiativeStat(derived.initiative, labels);
	const info = (key: string, title?: string) => termInfo(labels, key, title);
	const attacks = baseAttackRolls({ bac: derived.bac.total, bad: derived.bad.total }, labels);
	return [
		{
			title: terms.initiative.full,
			icon: "hourglass",
			tiles: [
				{
					label: stat ?? terms.initiative.full,
					term: info("initiative"),
					lines: sum(derived.initiative),
					value: derived.initiative.total,
					roll: testRoll(terms.initiative.full, derived.initiative.total),
				},
			],
		},
		{
			title: terms.armor_class.full,
			icon: "shieldFull",
			tiles: [{ label: terms.armor_class.abbr, term: info("ac"), lines: sum(derived.ac), value: derived.ac.total }],
		},
		{
			title: terms.attack_bonus.full,
			icon: "melee",
			tiles: [
				{
					label: terms.attack_bonus_melee.abbr,
					term: info("bac"),
					lines: sum(derived.bac),
					value: signed(derived.bac.total),
					roll: attacks.melee,
				},
				{
					label: terms.attack_bonus_ranged.abbr,
					term: info("bad"),
					lines: sum(derived.bad),
					value: signed(derived.bad.total),
					roll: attacks.ranged,
				},
			],
		},
		{
			title: terms.saving_throw.full,
			icon: "shieldHalf",
			tiles: [
				{
					label: terms.saving_throw_dexterity.abbr,
					term: info("jpd"),
					lines: sum(derived.jpd),
					value: derived.jpd.total,
					roll: testRoll(terms.saving_throw_dexterity.full, derived.jpd.total),
				},
				{
					label: terms.saving_throw_constitution.abbr,
					term: info("jpc"),
					lines: sum(derived.jpc),
					value: derived.jpc.total,
					roll: testRoll(terms.saving_throw_constitution.full, derived.jpc.total),
				},
				{
					label: terms.saving_throw_wisdom.abbr,
					term: info("jps"),
					lines: sum(derived.jps),
					value: derived.jps.total,
					roll: testRoll(terms.saving_throw_wisdom.full, derived.jps.total),
				},
			],
		},
		{
			title: terms.movement.full,
			icon: "movement",
			tiles: [
				{
					label: boxes.current,
					narrowLabel: terms.movement.abbr,
					term: info("movement"),
					lines: [
						`${boxes.current}: ${sumLine(derived.movement, labels)}`,
						`${boxes.run}: ${sumLine(derived.movement_run, labels)}`,
						`${boxes.climb}: ${sumLine(derived.movement_climb, labels)}`,
						`${boxes.swim}: ${sumLine(derived.movement_swim, labels)}`,
						`${boxes.fly}: ${flyLine}`,
					],
					value: meters(derived.movement.total),
				},
				{ label: boxes.run, term: info("run", boxes.run), lines: sum(derived.movement_run), value: meters(derived.movement_run.total), wideOnly: true },
				{
					label: boxes.climb,
					term: info("climb", boxes.climb),
					lines: sum(derived.movement_climb),
					value: meters(derived.movement_climb.total),
					wideOnly: true,
				},
				{ label: boxes.swim, term: info("swim", boxes.swim), lines: sum(derived.movement_swim), value: meters(derived.movement_swim.total), wideOnly: true },
				{ label: boxes.fly, term: info("fly", boxes.fly), lines: [flyLine], value: flying.total === 0 ? "—" : meters(flying.total), wideOnly: true },
			],
		},
	];
}

export function CombatStats({ view, labels }: { view: SheetView; labels: Labels }) {
	return (
		<div class="cs-combat">
			{groups(view, labels).map((group) => (
				<section key={group.title} class="cs-box cs-group">
					<h2 class="cs-title cs-group-title items-center justify-center gap-1.5 px-2 pb-0.5 pt-2 text-sm font-bold text-ink">
						<Icon name={group.icon} class="h-3.5 w-3.5 text-ink-muted" />
						{group.title}
					</h2>
					<div class="cs-tiles">
						{group.tiles.map((tile) => (
							<div key={tile.label} class={`cs-tile cs-box${tile.wideOnly ? " cs-wide-only" : ""}`}>
								<Term
									title={tile.term.title}
									text={tile.term.text}
									lines={tile.lines}
									label={
										tile.narrowLabel ? (
											<>
												<span class="cs-narrow-only">{tile.narrowLabel}</span>
												<span class="cs-wide-only">{tile.label}</span>
											</>
										) : (
											tile.label
										)
									}
								/>
								<RollChip spec={tile.roll ?? null}>
									<Pop value={tile.value} class="cs-num text-2xl leading-tight" />
								</RollChip>
							</div>
						))}
					</div>
				</section>
			))}
		</div>
	);
}
